/**
 * useTrustScore Hook
 * Score calculation helpers and color mapping for TrustGrid scores
 * Centralize trust/compliance/maturity score logic
 */

import { useMemo } from 'react';
import { colors } from '../theme/designTokens';

/**
 * Get color for a score value
 * @param {number} score - Score value (0-100)
 * @param {string} type - Score type ('trust' | 'compliance' | 'maturity')
 * @returns {string} Color hex code
 */
function getScoreColor(score, type = 'trust') {
  if (score === null || score === undefined) {
    return colors.neutral[400];
  }

  // Trust score tiers
  if (score >= 80) return colors.trustScore.excellent; // 80-100: Green
  if (score >= 60) return colors.trustScore.good; // 60-79: Amber
  return colors.trustScore.poor; // < 60: Red
}

/**
 * Get status for a score value
 * @param {number} score - Score value (0-100)
 * @returns {string} Status ('excellent' | 'good' | 'poor')
 */
function getScoreStatus(score) {
  if (score === null || score === undefined) return 'unknown';
  if (score >= 80) return 'excellent';
  if (score >= 60) return 'good';
  return 'poor';
}

/**
 * Get label for a score status
 * @param {number} score - Score value (0-100)
 * @returns {string} Label text
 */
function getScoreLabel(score) {
  if (score === null || score === undefined) return 'Unknown';
  if (score >= 80) return 'Excellent';
  if (score >= 60) return 'Good';
  return 'Needs Attention';
}

/**
 * Format score for display
 * @param {number} score - Score value (0-100)
 * @param {Object} options - Formatting options
 * @returns {string} Formatted score
 */
function formatScore(score, options = {}) {
  const { decimals = 0, suffix = '', prefix = '' } = options;

  if (score === null || score === undefined) return 'N/A';

  const formatted = score.toFixed(decimals);
  return `${prefix}${formatted}${suffix}`;
}

/**
 * Calculate score delta (change from previous)
 * @param {number} current - Current score
 * @param {number} previous - Previous score
 * @returns {Object} { delta, percentage, isPositive }
 */
function calculateScoreDelta(current, previous) {
  if (current === null || current === undefined || previous === null || previous === undefined) {
    return { delta: 0, percentage: 0, isPositive: false };
  }

  const delta = current - previous;
  const percentage = previous !== 0 ? (delta / previous) * 100 : 0;
  const isPositive = delta > 0;

  return { delta, percentage, isPositive };
}

/**
 * Get score tier info (for gauge/progress visualization)
 * @param {number} score - Score value (0-100)
 * @returns {Object} Tier information
 */
function getScoreTier(score) {
  const status = getScoreStatus(score);
  const color = getScoreColor(score);
  const label = getScoreLabel(score);

  return {
    status,
    color,
    label,
    range: status === 'excellent' ? '80-100' : status === 'good' ? '60-79' : '0-59',
  };
}

/**
 * useTrustScore hook
 * Provides score calculation and formatting helpers
 *
 * @param {number} score - Score value (0-100)
 * @param {Object} options - Configuration options
 * @returns {Object} Score utilities and computed values
 */
export default function useTrustScore(score, options = {}) {
  const { type = 'trust', previousScore = null } = options;

  // Computed values
  const color = useMemo(() => getScoreColor(score, type), [score, type]);
  const status = useMemo(() => getScoreStatus(score), [score]);
  const label = useMemo(() => getScoreLabel(score), [score]);
  const tier = useMemo(() => getScoreTier(score), [score]);
  const formatted = useMemo(() => formatScore(score), [score]);

  // Delta calculation (if previous score provided)
  const delta = useMemo(() => {
    if (previousScore !== null) {
      return calculateScoreDelta(score, previousScore);
    }
    return null;
  }, [score, previousScore]);

  return {
    // Raw score
    score,

    // Computed
    color,
    status,
    label,
    tier,
    formatted,
    delta,

    // Helper functions (for use elsewhere)
    getColor: (s) => getScoreColor(s, type),
    getStatus: getScoreStatus,
    getLabel: getScoreLabel,
    getTier: getScoreTier,
    format: formatScore,
    calculateDelta: calculateScoreDelta,
  };
}

// Export helper functions for standalone use
export {
  getScoreColor,
  getScoreStatus,
  getScoreLabel,
  getScoreTier,
  formatScore,
  calculateScoreDelta,
};
